/**
 * Habit Check-In page.
 *
 * A guided, time-aware queue of currently-relevant habits with expressive
 * responses (completed / missed / partial / skipped / snoozed / …). The queue
 * is deliberately independent of the Analytics Reference Date: it sends only
 * the real `tz_offset` and never imports getAnalyticsReferenceDateParams.
 *
 * For documentation, see docs/42 HABIT_CHECKIN_ARCHITECTURE.md and
 * docs/60 USER_GUIDE.md.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Typography,
  Alert,
  AlertTitle,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemText,
  Menu,
  Popover,
  MenuItem,
  Stack,
  Divider,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import {
  QUEUE_CADENCE_ENTRIES,
  QUEUE_CADENCE_TITLE,
} from '../checkin/queueCadence';
import {
  CHECK_IN_ACTIVITY_PATH,
  CHECK_IN_INSIGHT_PATH,
  CheckInActivityResponse,
  CheckInQueueInsightResponse,
} from '../checkin/insightTypes';
import axios from 'axios';
import { getApiErrorMessage } from '../utils/apiError';
import MetricInfo from '../components/MetricInfo';
import { formatStreak } from '../metrics/streakFormat';
import { usePreferences } from '../contexts/PreferencesContext';
import HabitQuickLogChips, {
  QuickLogHabit,
  isMeasurableKind,
  quickIncrementValues,
} from '../components/HabitQuickLogChips';
import PageShell from '../components/PageShell';
import ViewModeToggle from '../components/ViewModeToggle';
import { usePersistentViewMode } from '../hooks/usePersistentViewMode';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const REFRESH_MS = 30000;

export interface CheckInOption {
  id: number;
  response_category: string;
  label: string;
  score_value: number | null;
  /** Served but hidden from the one-tap row when false — still offered in
   * the "More…" menu (migration 0028). */
  show_in_queue?: boolean;
  is_user_defined?: boolean;
}

export interface CheckInQueueItem {
  /** THE routing key: 'habit' | 'commitment'. Never route on `item_kind` —
   * that is a rendering hint and stays an unconstrained string. Optional
   * because an older server served no such field; absent reads as a habit. */
  target_type?: string;
  /** The id inside `target_type`'s own domain: habits.id for a habit slot,
   * commitment_occurrences.id for a commitment slot. */
  target_id?: number;
  /** NULL for a commitment — it is not a habit, and /api/check-ins/respond
   * must never be handed this id. Still populated (== target_id) for every
   * habit slot. */
  habit_id: number | null;
  title: string;
  /** NULL for a commitment: a block of time has no habit recurrence. */
  recurrence_type: string | null;
  /** Neutral filler on a commitment slot (always 1 / 0-then-1 / 0), not real
   * commitment data — never rendered as progress or priority there. */
  target_count: number;
  completed_count: number;
  priority: number;
  /** Live: completed after the snapshot — stays in its slot, marked done. */
  done: boolean;
  window_label: string | null;
  in_window: boolean;
  missed_window: boolean;
  /** Live: the time-of-day window opens later today ("opens at …"). */
  pending_window: boolean;
  window_start_minute: number | null;
  window_end_minute: number | null;
  /** Live: active occurrence snooze — held in place until this instant. */
  snooze_return_at: string | null;
  /**
   * Live: the most recent response recorded today, or null if unanswered.
   * Skip / Missed / Not applicable / Deferred write a CheckInEvent but no
   * HabitLog, so `done` cannot represent them — without this the card looks
   * untouched after a successful response and the button reads as dead.
   */
  last_response: string | null;
  /** The non-retracted event backing last_response — the bounded-undo
   * target (POST /api/check-ins/events/{id}/retract). */
  last_response_event_id?: number | null;
  /** 'current' (default) for a frozen current-day slot, 'retro' for a
   * catch-up offer on a past closed period, 'debt' for an owed amount from
   * the debt ledger (Lane D), 'commitment' for a temporal-commitment
   * confirmation (Lane E). A RENDERING HINT ONLY — routing is target_type. */
  item_kind?: string;
  /** Retro: the offered period's last calendar day (YYYY-MM-DD). */
  effective_date?: string | null;
  period_start?: string | null;
  /** Exclusive, like the server's period convention. */
  period_end?: string | null;
  /** Debt: outstanding amount for this row (MERGE total or STACK slice). */
  debt_amount?: number | null;
  /** The snapshot's own logical day (YYYY-MM-DD). Between local midnight and
   * the cutoff hour the calendar day has rolled while the queue has not, so
   * non-retro responses are stamped with THIS served day — a calendar-day
   * stamp would land the HabitLog on the next period and the card would read
   * answered-but-still-due until the cutoff. Optional: an older server omits
   * it and the stamp falls back to the local calendar day. */
  snapshot_date?: string | null;
  /** Frozen by the daily snapshot: overall position and batch of 8. */
  rank: number;
  batch: number;
  /** 0-based index within this item's own batch. */
  batch_position: number;
  queue_position: number;
  /** Server truth for batch completion: an explicit answer exists — including
   * answered-but-not-done outcomes (Skip / Missed / an answered-Missed
   * commitment), which `done` cannot represent. Optional: an older server
   * omits it, which reads as unanswered. */
  answered?: boolean;
  /** Current habit slots only (D-P5-3): the live streak counted in the
   * habit's own recurrence periods, plus the interval that names the unit. */
  current_streak?: number | null;
  recurrence_interval?: number | null;
  /** MERGE debt rows only: one served row stands in for this many owed
   * periods, the oldest of which starts here (scope extra d). */
  covered_period_count?: number | null;
  oldest_period_start?: string | null;
  options: CheckInOption[];
  explanation: string;
}

/**
 * The commitment facts the queue payload deliberately does NOT carry, joined
 * client-side by occurrence id: whether a reschedule is even offered, where
 * the block really sits (for the partial-span defaults) and the parent's own
 * snooze offsets. Sourced from GET /api/commitments/occurrences (which
 * denormalises `reschedulable` and `title` for exactly this) plus
 * GET /api/commitments for the offsets. A failure degrades to no Reschedule
 * affordance — never to a Reschedule that cannot be honoured.
 */
interface CommitmentOccurrenceMeta {
  id: number;
  commitment_id: number;
  planned_start_at: string;
  planned_end_at: string;
  reschedulable: boolean;
  snooze_offsets_minutes: number[] | null;
  carryover_policy: string | null;
}

/** POST /api/commitments/occurrences/{id}/snooze-preview — "where would this
 * land?", answered without writing anything. */
export interface SnoozePreview {
  status: string;
  reason: string;
  resolved: boolean;
  start_at: string | null;
  end_at: string | null;
  rolled: boolean;
  rolled_minutes: number;
  warnings: string[];
  carryover_policy: string | null;
  carryover_obligation_count: number | null;
  carryover_suggested_duration_minutes: number | null;
}

/** Session-only tag muting ("I'm at the office, don't surface relationship
 * habits"). sessionStorage on purpose: it clears when the browser session
 * ends, and it is the ONLY thing muting touches — muting must write no
 * CheckInEvent rows, no snoozes, no server state of any kind. */
export const CHECK_IN_MUTED_TAGS_KEY = 'kalpa-check-in-muted-tags';

const loadMutedTags = (): string[] => {
  try {
    const raw = sessionStorage.getItem(CHECK_IN_MUTED_TAGS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((tag): tag is string => typeof tag === 'string')
      : [];
  } catch {
    return [];
  }
};

/** Chips for responses that record an event but write no habit log. These are
 * the categories that were previously invisible: the POST succeeded, the
 * ledger row landed, and not a single rendered pixel changed. */
const RESPONSE_CHIP_LABELS: Record<string, string> = {
  skipped: 'Skipped',
  missed: 'Missed',
  not_applicable: 'Not applicable',
  deferred: 'Deferred',
};

const SNOOZE_PRESETS: { label: string; minutes: number | null }[] = [
  { label: '15 minutes', minutes: 15 },
  { label: '1 hour', minutes: 60 },
  { label: '2 hours', minutes: 120 },
  { label: 'Until tomorrow', minutes: null }, // computed: minutes to next local midnight
];

/** Minutes from now until the next local midnight (for "Until tomorrow"). */
const minutesUntilTomorrow = (): number => {
  const now = new Date();
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
  return Math.max(1, Math.round((tomorrow.getTime() - now.getTime()) / 60000));
};

/** Local calendar day as YYYY-MM-DD (never a UTC instant). */
const todayLocalYmd = (): string => {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

/** YYYY-MM-DD plus a day delta, in local calendar space (no UTC drift). */
const addDaysYmd = (ymd: string, delta: number): string => {
  const [y, m, d] = ymd.split('-').map(Number);
  const date = new Date(y, (m || 1) - 1, (d || 1) + delta);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/** The earlier of two YYYY-MM-DD strings (ISO order is lexicographic). */
const minYmd = (a: string, b: string): string => (a < b ? a : b);

/** Whole local days between a past YYYY-MM-DD and today (0 for today). */
const daysAgo = (ymd: string): number => {
  const [y, m, d] = ymd.split('-').map(Number);
  const then = new Date(y, (m || 1) - 1, d || 1).getTime();
  const [ty, tm, td] = todayLocalYmd().split('-').map(Number);
  const today = new Date(ty, tm - 1, td).getTime();
  return Math.max(0, Math.round((today - then) / 86400000));
};

/** "Aug 6" for a YYYY-MM-DD, in the viewer's locale (short month, no year). */
const formatYmdShort = (ymd: string): string => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
  });
};

/**
 * The honest empty/finished-state specifics (W3.2): fold the insight's
 * per-habit reason codes into short sentences about the user's own data —
 * never the one-size-fits-all cheer that hid genuine configuration gaps.
 * Reasons outside the ratified vocabulary are silently skipped: an honest
 * report must not guess.
 */
const insightReasonLines = (insight: CheckInQueueInsightResponse): string[] => {
  const lines: string[] = [];
  const habits = insight.habits ?? [];
  const withReason = (reason: string) => habits.filter((h) => h.reason === reason);
  const habitsHave = (n: number) => (n === 1 ? '1 habit has' : `${n} habits have`);

  const off = withReason('mode_none').length;
  if (off > 0) lines.push(`${habitsHave(off)} backfill off`);

  const complete = withReason('all_periods_complete');
  if (complete.length > 0) {
    // Name the window only when every such habit shares one reach — a mixed
    // set would otherwise print a number that is wrong for some of them.
    const reaches = Array.from(
      new Set(complete.map((h) => h.reach_days).filter((d): d is number => d !== null)),
    );
    const windowNote = reaches.length === 1 ? ` (${reaches[0]} days)` : '';
    lines.push(
      `${habitsHave(complete.length)} nothing missed within ${
        complete.length === 1 ? 'its' : 'their'
      } reach window${windowNote}`,
    );
  }

  const alreadyAnswered = withReason('all_periods_answered').length;
  if (alreadyAnswered > 0) {
    lines.push(
      `${
        alreadyAnswered === 1 ? '1 habit' : `${alreadyAnswered} habits`
      } already answered every missed period`,
    );
  }

  const outstanding = withReason('nothing_outstanding').length;
  if (outstanding > 0) lines.push(`${habitsHave(outstanding)} no outstanding debt`);

  const unclosed = withReason('no_closed_period_yet').length;
  if (unclosed > 0) lines.push(`${habitsHave(unclosed)} no closed period to catch up yet`);

  const anchored = withReason('no_debt_since_anchor');
  if (anchored.length > 0) {
    const anchors = Array.from(
      new Set(anchored.map((h) => h.anchor_date).filter((d): d is string => Boolean(d))),
    );
    const since =
      anchors.length === 1 ? formatYmdShort(anchors[0]) : "each habit's own start date";
    lines.push(
      `Debt for ${
        anchored.length === 1 ? '1 habit' : `${anchored.length} habits`
      } accrues only since ${since}`,
    );
  }

  const paused = withReason('paused').length;
  if (paused > 0) {
    lines.push(`${paused === 1 ? '1 habit is' : `${paused} habits are`} paused`);
  }

  const snoozed = withReason('snoozed').length;
  if (snoozed > 0) {
    lines.push(
      `${snoozed === 1 ? '1 habit is' : `${snoozed} habits are`} snoozed right now`,
    );
  }

  const opaque = withReason('opaque_blocked').length;
  if (opaque > 0) {
    lines.push(
      `${
        opaque === 1 ? '1 habit measures' : `${opaque} habits measure`
      } opaquely, so debt cannot accrue`,
    );
  }

  // Capacity: only the per-habit no_capacity gate means "didn't fit" —
  // suppressed_backfill_count counts FROZEN entries not currently served
  // (the reveal rule, settled debt, render-time drops), all of which DID
  // fit the batches, so it must never be printed as a capacity miss.
  const noCapacity = withReason('no_capacity').length;
  if (noCapacity > 0) {
    lines.push(
      `Catch-up for ${
        noCapacity === 1 ? '1 habit' : `${noCapacity} habits`
      } didn't fit today's batches`,
    );
  }

  // Frozen-but-hidden catch-up is reportable only while the reveal rule is
  // what hides it — due-now work still open. Settled or dropped rows earn
  // no line: "you already settled it" is not a reason worth printing.
  const suppressed = insight.queue?.suppressed_backfill_count ?? 0;
  if (suppressed > 0 && insight.queue?.due_now_unanswered === true) {
    lines.push(
      `${suppressed} catch-up item${suppressed === 1 ? '' : 's'} unlock${
        suppressed === 1 ? 's' : ''
      } after you finish today's work`,
    );
  }

  return lines;
};

/** The queue is polymorphic: route on target_type, NEVER on item_kind. */
const isCommitmentItem = (item: CheckInQueueItem): boolean =>
  item.target_type === 'commitment';

/** The id inside the slot's own domain. Falls back to habit_id so an older
 * server (no target_id on the wire) keeps working for habit slots. */
const targetIdOf = (item: CheckInQueueItem): number =>
  item.target_id ?? item.habit_id ?? -1;

/** habitMeta is keyed by habit_id, which a commitment slot does not have —
 * every lookup goes through here so `undefined` is the only miss. */
const metaOf = (
  habitMeta: Record<number, QuickLogHabit & { tags?: string[] }>,
  item: CheckInQueueItem,
): (QuickLogHabit & { tags?: string[] }) | undefined =>
  item.habit_id === null || item.habit_id === undefined
    ? undefined
    : habitMeta[item.habit_id];

/** Stable identity for a rendered slot. habit_id alone was never enough
 * (retro/debt rows repeat one) and is NULL for a commitment, which would
 * collide every commitment onto the same key — so the domain pair leads. */
const itemKeyOf = (item: CheckInQueueItem): string =>
  `${item.target_type ?? 'habit'}:${targetIdOf(item)}:${
    item.item_kind ?? 'current'
  }:${item.effective_date ?? item.period_start ?? ''}`;

/** The four option-less custom-score categories (Lane E1a). "Don't recall"
 * maps to UNSURE — an event-only category that never writes a HabitLog. */
const CUSTOM_SCORE_CATEGORIES: { value: string; label: string }[] = [
  { value: 'completed', label: 'Done' },
  { value: 'partial', label: 'Partly' },
  { value: 'missed', label: 'No' },
  { value: 'unsure', label: "Don't recall" },
];

/** "6:00 PM" for a window's minutes-since-local-midnight value. */
const formatMinuteOfDay = (minute: number): string => {
  const d = new Date();
  d.setHours(Math.floor(minute / 60) % 24, minute % 60, 0, 0);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

/** Local wall-clock time of an ISO instant (for snooze return times). */
const formatInstant = (iso: string): string =>
  new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const pad2 = (value: number): string => String(value).padStart(2, '0');

/** "17:00" for minutes since the account's local midnight. */
const minutesToClock = (minute: number): string =>
  `${pad2(Math.floor(minute / 60) % 24)}:${pad2(minute % 60)}`;

/** The committed block as text. `window_label` is already "HH:MM-HH:MM"; the
 * minutes are the fallback for a server that stopped sending it. */
const commitmentSpanLabel = (item: CheckInQueueItem): string | null => {
  if (item.window_label) return item.window_label;
  if (
    item.window_start_minute === null ||
    item.window_start_minute === undefined ||
    item.window_end_minute === null ||
    item.window_end_minute === undefined
  ) {
    return null;
  }
  return `${minutesToClock(item.window_start_minute)}-${minutesToClock(
    item.window_end_minute,
  )}`;
};

/** Offered only when the commitment itself carries no snooze offsets. */
const DEFAULT_SNOOZE_OFFSETS = [30, 60, 120, 1440];

/** "30 minutes" / "2 hours" / "1 day" for a positive minute offset. */
const offsetLabel = (minutes: number): string => {
  if (minutes % 1440 === 0) {
    const days = minutes / 1440;
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
};

/** A landing slot reads as a weekday + date + time — a snooze can roll days. */
const formatLanding = (iso: string): string =>
  new Date(iso).toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/** An instant as the local "YYYY-MM-DDTHH:MM" a datetime-local field wants. */
const toLocalInputValue = (value: Date): string =>
  `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(
    value.getDate(),
  )}T${pad2(value.getHours())}:${pad2(value.getMinutes())}`;

/** Where the partial-span picker opens: the occurrence's own planned instants
 * when they are known, else the served window on the queue's logical day (an
 * end at or before the start is the block running past midnight). */
const defaultSpanFor = (
  item: CheckInQueueItem,
  meta: CommitmentOccurrenceMeta | undefined,
): { start: string; end: string } => {
  if (meta) {
    return {
      start: toLocalInputValue(new Date(meta.planned_start_at)),
      end: toLocalInputValue(new Date(meta.planned_end_at)),
    };
  }
  const [y, m, d] = (item.snapshot_date ?? todayLocalYmd()).split('-').map(Number);
  const startMinute = item.window_start_minute ?? 0;
  const endMinute = item.window_end_minute ?? startMinute + 60;
  const start = new Date(
    y,
    (m || 1) - 1,
    d || 1,
    Math.floor(startMinute / 60),
    startMinute % 60,
  );
  const end = new Date(
    y,
    (m || 1) - 1,
    (d || 1) + (endMinute <= startMinute ? 1 : 0),
    Math.floor(endMinute / 60),
    endMinute % 60,
  );
  return { start: toLocalInputValue(start), end: toLocalInputValue(end) };
};

/** What the unplaceable obligation would do under the commitment's own
 * carryover policy — the choice the user is offered instead of a slot. */
const carryoverSentence = (preview: SnoozePreview): string | null => {
  if (!preview.carryover_policy) return null;
  const parts = [
    preview.carryover_policy === 'stack'
      ? 'Carryover stacks one obligation per period'
      : 'Carryover merges into a single obligation',
  ];
  if (
    preview.carryover_obligation_count !== null &&
    preview.carryover_obligation_count !== undefined
  ) {
    parts.push(`${preview.carryover_obligation_count} outstanding`);
  }
  if (preview.carryover_suggested_duration_minutes) {
    parts.push(
      `suggested ${offsetLabel(preview.carryover_suggested_duration_minutes)}`,
    );
  }
  return `${parts.join(' · ')}.`;
};

type BtnColor = 'success' | 'error' | 'info' | 'warning' | 'inherit';

const optionColor = (category: string): BtnColor => {
  switch (category) {
    case 'completed':
      return 'success';
    case 'missed':
      return 'error';
    case 'snoozed':
      return 'info';
    case 'partial':
      return 'warning';
    default:
      return 'inherit';
  }
};

const CheckIn: React.FC = () => {
  const { pageHeaderSx, cardContentPaddingSx, panelPaddingSx } = useLayoutTokens();
  const [viewMode, setViewMode, viewModeControls] = usePersistentViewMode('check-in');
  const [items, setItems] = useState<CheckInQueueItem[]>([]);
  // Measurement metadata AND tags per habit — the queue payload itself stays
  // lean, so both are joined client-side by habit_id from GET /api/habits/.
  const [habitMeta, setHabitMeta] = useState<
    Record<number, QuickLogHabit & { tags?: string[] }>
  >({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Success acknowledgement for the debt actions. A served drawdown goes
  // LIVE server-side and a settled card drops on refetch — this note is the
  // only feedback left standing, so it must exist.
  const [notice, setNotice] = useState<string | null>(null);
  // Per-SLOT busy state, keyed by itemKeyOf. habit_id was never a slot
  // identity (retro/debt rows repeat one, so they froze together) and a
  // commitment has none at all.
  const [respondingKey, setRespondingKey] = useState<string | null>(null);
  const [cardIndex, setCardIndex] = useState(0);
  const [snoozeCtx, setSnoozeCtx] = useState<
    { anchor: HTMLElement; item: CheckInQueueItem; option: CheckInOption } | null
  >(null);
  // Session-only tag muting: pure view-level state, hydrated from and
  // mirrored to sessionStorage. Never causes a network write.
  const [mutedTags, setMutedTags] = useState<string[]>(loadMutedTags);
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [muteMenuAnchor, setMuteMenuAnchor] = useState<HTMLElement | null>(null);
  const [cadenceAnchor, setCadenceAnchor] = useState<HTMLElement | null>(null);
  // "More…" menu (every active option, hidden ones included, + custom score).
  const [moreCtx, setMoreCtx] = useState<
    { anchor: HTMLElement; item: CheckInQueueItem } | null
  >(null);
  // Custom-score dialog: an option-less response for one item.
  const [customCtx, setCustomCtx] = useState<CheckInQueueItem | null>(null);
  const [customScore, setCustomScore] = useState('');
  const [customCategory, setCustomCategory] = useState('partial');
  // Debt forgiveness confirm dialog.
  const [forgiveCtx, setForgiveCtx] = useState<CheckInQueueItem | null>(null);
  // Per-slot draft inputs: the retro card's picked past day and the debt
  // card's drawdown amount, keyed by itemKeyOf.
  const [retroDates, setRetroDates] = useState<Record<string, string>>({});
  const [debtAmounts, setDebtAmounts] = useState<Record<string, string>>({});
  // Commitment slots (Lane E). Meta is keyed by occurrence id (== target_id).
  const [commitmentMeta, setCommitmentMeta] = useState<
    Record<number, CommitmentOccurrenceMeta>
  >({});
  // Partial-span picker: the real span a partly-kept block actually ran.
  const [partialCtx, setPartialCtx] = useState<CheckInQueueItem | null>(null);
  const [partialStart, setPartialStart] = useState('');
  const [partialEnd, setPartialEnd] = useState('');
  // Reschedule: pick an offset, SEE where it lands, then commit.
  const [rescheduleCtx, setRescheduleCtx] = useState<CheckInQueueItem | null>(null);
  const [rescheduleMinutes, setRescheduleMinutes] = useState<number | null>(null);
  const [preview, setPreview] = useState<SnoozePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const navigate = useNavigate();
  // The check_in preferences namespace (show_completed_batches). null when no
  // provider is mounted — the toggle then lives in session state only.
  const preferences = usePreferences();
  // Header stats (W2.2) and the settled-queue insight (W3.2).
  const [activity, setActivity] = useState<CheckInActivityResponse | null>(null);
  const [insight, setInsight] = useState<CheckInQueueInsightResponse | null>(null);
  const [activityAnchor, setActivityAnchor] = useState<HTMLElement | null>(null);
  // Controlled batch expansion (list mode), keyed by frozen batch index.
  // Session-only by design — only the show-completed choice persists.
  const [batchExpansion, setBatchExpansion] = useState<Record<number, boolean>>({});
  const [showCompletedFallback, setShowCompletedFallback] = useState(false);

  // Real timezone only — the queue must never receive an as_of_date.
  const tzOffset = useMemo(() => new Date().getTimezoneOffset(), []);

  const fetchQueue = useCallback(
    async (opts?: { silent?: boolean }): Promise<CheckInQueueItem[] | null> => {
      if (!opts?.silent) setLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/api/check-ins/queue`, {
          params: { tz_offset: tzOffset },
        });
        setItems(res.data);
        setError(null);
        // Returned so a caller can act on the fresh rows (card auto-advance)
        // without waiting a render for the state to land.
        return res.data as CheckInQueueItem[];
      } catch (err: any) {
        setError(getApiErrorMessage(err, 'Failed to load the check-in queue'));
        return null;
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    [tzOffset],
  );

  // Header stats (streak + checked-in counts). A failure — or an older server
  // without the endpoint, or a mock serving the wrong shape — degrades to no
  // stats line; the queue itself is untouched.
  const fetchActivity = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}${CHECK_IN_ACTIVITY_PATH}`, {
        params: { tz_offset: tzOffset },
      });
      const data = res?.data;
      setActivity(
        data && typeof data.streak_current === 'number' && data.today && data.checked_in
          ? (data as CheckInActivityResponse)
          : null,
      );
    } catch {
      setActivity(null);
    }
  }, [tzOffset]);

  // Measurable-habit metadata for the chip rows / opaque fields. A failure
  // degrades to no quick-log controls — the queue's own options still work.
  const fetchHabitMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/habits/`, {
        params: { include_today: true, tz_offset: tzOffset },
      });
      const map: Record<number, QuickLogHabit & { tags?: string[] }> = {};
      (Array.isArray(res.data) ? res.data : []).forEach(
        (habit: QuickLogHabit & { id?: number; tags?: string[] }) => {
          if (typeof habit?.id === 'number') map[habit.id] = habit;
        },
      );
      setHabitMeta(map);
    } catch {
      setHabitMeta({});
    }
  }, [tzOffset]);

  // The user's habit tags power the mute control's option list. A failed
  // request degrades to no mute control — the queue itself still works.
  const fetchTagOptions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/habit-tags/`);
      const names = Array.isArray(res.data)
        ? res.data
            .map((tag: { name?: unknown }) => tag?.name)
            .filter(
              (name: unknown): name is string =>
                typeof name === 'string' && name.length > 0,
            )
        : [];
      setTagOptions(names);
    } catch {
      setTagOptions([]);
    }
  }, []);

  /**
   * Join the commitment facts the queue payload does not carry.
   *
   * The occurrence range is EXACTLY the queue's own logical day — the same
   * single day the queue build already materialises — so this read widens
   * nothing: every commitment slot in the queue is a PLANNED occurrence
   * dated on that day. A failure leaves the map empty, which costs the
   * Reschedule affordance and the exact partial-span defaults, and nothing
   * else: Honored / Partially honored / Not honored keep working.
   */
  const fetchCommitmentMeta = useCallback(async (day: string) => {
    try {
      const [occurrences, commitments] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/commitments/occurrences`, {
          params: { from: day, to: day },
        }),
        axios.get(`${API_BASE_URL}/api/commitments`),
      ]);
      const parents: Record<
        number,
        { snooze_offsets_minutes?: number[] | null; carryover_policy?: string }
      > = {};
      (Array.isArray(commitments.data) ? commitments.data : []).forEach(
        (row: Record<string, any>) => {
          if (typeof row?.id === 'number') parents[row.id] = row;
        },
      );
      const map: Record<number, CommitmentOccurrenceMeta> = {};
      (Array.isArray(occurrences.data) ? occurrences.data : []).forEach(
        (occurrence: Record<string, any>) => {
          if (typeof occurrence?.id !== 'number') return;
          const parent = parents[occurrence.commitment_id] ?? {};
          map[occurrence.id] = {
            id: occurrence.id,
            commitment_id: occurrence.commitment_id,
            planned_start_at: occurrence.planned_start_at,
            planned_end_at: occurrence.planned_end_at,
            reschedulable: Boolean(occurrence.reschedulable),
            snooze_offsets_minutes: Array.isArray(parent.snooze_offsets_minutes)
              ? parent.snooze_offsets_minutes
              : null,
            carryover_policy:
              typeof parent.carryover_policy === 'string'
                ? parent.carryover_policy
                : null,
          };
        },
      );
      setCommitmentMeta(map);
    } catch {
      setCommitmentMeta({});
    }
  }, []);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  useEffect(() => {
    fetchHabitMeta();
  }, [fetchHabitMeta]);

  useEffect(() => {
    fetchTagOptions();
  }, [fetchTagOptions]);

  // The logical day to join commitment meta for, or null when the queue holds
  // no commitment at all — an all-habits queue must issue no extra request.
  // Every commitment slot is dated on the queue's own day, so one day covers
  // them all, and the memo returns a stable string across silent refetches.
  const commitmentDay = useMemo(() => {
    const item = items.find(isCommitmentItem);
    if (!item) return null;
    return item.snapshot_date ?? todayLocalYmd();
  }, [items]);

  useEffect(() => {
    if (!commitmentDay) return;
    fetchCommitmentMeta(commitmentDay);
  }, [commitmentDay, fetchCommitmentMeta]);

  // Mirror the muted set to sessionStorage so it survives a reload within
  // this browser session — and only that. No server round-trip, ever.
  useEffect(() => {
    try {
      sessionStorage.setItem(CHECK_IN_MUTED_TAGS_KEY, JSON.stringify(mutedTags));
    } catch {
      // Inaccessible storage just means muting is tab-lifetime only.
    }
  }, [mutedTags]);

  const toggleMutedTag = (tag: string) => {
    setMutedTags((prev) =>
      prev.includes(tag) ? prev.filter((entry) => entry !== tag) : [...prev, tag],
    );
  };

  const mutedSet = useMemo(() => new Set(mutedTags), [mutedTags]);

  // VIEW-LEVEL filter: muted items simply don't render. The frozen ranks
  // already tolerate gaps (completing others never renumbers), so hiding a
  // slot is safe; unmuting restores it exactly where it was. Kept callable so
  // the card auto-advance can apply the same lens to freshly-fetched rows.
  const filterVisibleItems = useCallback(
    (rows: CheckInQueueItem[]): CheckInQueueItem[] => {
      if (mutedSet.size === 0) return rows;
      return rows.filter((item) => {
        // A commitment carries no habit_id and therefore no habit tags:
        // muting a tag can never hide one.
        const tags = metaOf(habitMeta, item)?.tags;
        return !(Array.isArray(tags) && tags.some((tag) => mutedSet.has(tag)));
      });
    },
    [habitMeta, mutedSet],
  );
  const visibleItems = useMemo(
    () => filterVisibleItems(items),
    [filterVisibleItems, items],
  );
  const mutedOutCount = items.length - visibleItems.length;

  // W3.2: the queue is "settled" when a SUCCESSFUL load served nothing
  // actionable — empty, or every served item explicitly answered. `answered`
  // is the server's truth for retro catch-up items exactly as for current
  // ones (answered retro rows stay in their frozen slots all day), and debt
  // rows never carry it (settled debt drops server-side), so the one check
  // covers every kind. A failed load settles nothing: an error must never
  // read as an empty day. Only when settled is the insight read worth a
  // request — it explains, from the user's own data, why nothing more is on
  // offer.
  const queueSettled = useMemo(
    () =>
      !loading &&
      !error &&
      (items.length === 0 || items.every((item) => item.answered === true)),
    [items, loading, error],
  );

  useEffect(() => {
    if (!queueSettled) {
      setInsight(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}${CHECK_IN_INSIGHT_PATH}`, {
          params: { tz_offset: tzOffset },
        });
        const data = res?.data;
        if (!cancelled) {
          // Shape-guarded: an older server (or a blanket test mock) serving
          // something else must degrade to "render nothing extra".
          setInsight(data && data.queue && Array.isArray(data.habits) ? data : null);
        }
      } catch {
        if (!cancelled) setInsight(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [queueSettled, tzOffset]);

  // Periodic silent refresh so window open/close and snooze expiry re-evaluate
  // server-side (the server owns due-ness) without a manual reload.
  useEffect(() => {
    const id = setInterval(() => fetchQueue({ silent: true }), REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchQueue]);

  // Keep the card cursor in range as the queue shrinks after responses (or
  // as muting hides items from view).
  useEffect(() => {
    setCardIndex((i) =>
      visibleItems.length === 0 ? 0 : Math.min(i, visibleItems.length - 1),
    );
  }, [visibleItems]);

  // Scope extra c: after a successful answer in card mode, step the cursor
  // forward to the next still-unanswered slot (skipping items answered from
  // another device meanwhile). A CURSOR move only — the answered card keeps
  // its frozen slot and stays reachable via Back — and it never wraps: with
  // nothing unanswered ahead the cursor stays put.
  const advanceCardPast = useCallback(
    (rows: CheckInQueueItem[] | null) => {
      if (viewMode !== 'grid' || !Array.isArray(rows)) return;
      const fresh = filterVisibleItems(rows);
      setCardIndex((i) => {
        for (let j = i + 1; j < fresh.length; j += 1) {
          if (fresh[j]?.answered !== true) return j;
        }
        return fresh.length === 0 ? 0 : Math.min(i, fresh.length - 1);
      });
    },
    [viewMode, filterVisibleItems],
  );

  // The picked catch-up day for a retro slot (defaults to the served
  // effective day — the period's last calendar day).
  const retroDateFor = useCallback(
    (item: CheckInQueueItem): string =>
      retroDates[itemKeyOf(item)] ?? item.effective_date ?? todayLocalYmd(),
    [retroDates],
  );

  const respond = useCallback(
    async (
      item: CheckInQueueItem,
      response: {
        response_category: string;
        option_id?: number;
        presented_option_label?: string;
        custom_score?: number;
      },
      extra?: Record<string, unknown>,
    ) => {
      // A commitment answer goes to /api/commitments/occurrences/{id}/respond
      // — handing this endpoint an occurrence id 404s "Habit not found".
      if (isCommitmentItem(item) || item.habit_id === null) return;
      setRespondingKey(itemKeyOf(item));
      try {
        await axios.post(`${API_BASE_URL}/api/check-ins/respond`, {
          habit_id: item.habit_id,
          ...response,
          // Retro (catch-up) answers land on the picked past day; everything
          // else lands on the queue's own served logical day (the calendar
          // day is wrong between midnight and the cutoff), falling back to
          // today-local when an older server omits snapshot_date.
          date_local:
            item.item_kind === 'retro'
              ? retroDateFor(item)
              : (item.snapshot_date ?? todayLocalYmd()),
          tz_offset: tzOffset,
          window_label: item.window_label,
          in_window: item.in_window,
          queue_position: item.queue_position,
          ...extra,
        });
        // Refetch so the item's live state (done / snoozed / last_response)
        // re-renders and multiple devices converge. Items do NOT drop out —
        // the server holds every answered item in its frozen slot for the rest
        // of the logical day, which is exactly why the response has to be
        // visible as state rather than as a disappearance.
        const rows = await fetchQueue({ silent: true });
        fetchActivity();
        advanceCardPast(rows);
      } catch (err: any) {
        setError(getApiErrorMessage(err, 'Failed to record your response'));
      } finally {
        setRespondingKey(null);
      }
    },
    [tzOffset, fetchQueue, retroDateFor, fetchActivity, advanceCardPast],
  );

  /** The payload shape a chosen option contributes to respond(). */
  const optionResponse = (option: CheckInOption) => ({
    response_category: option.response_category,
    option_id: option.id,
    presented_option_label: option.label,
  });

  const handleOption = (
    event: React.MouseEvent<HTMLElement>,
    item: CheckInQueueItem,
    option: CheckInOption,
  ) => {
    if (option.response_category === 'snoozed') {
      setSnoozeCtx({ anchor: event.currentTarget, item, option });
      return;
    }
    respond(item, optionResponse(option));
  };

  const pickSnooze = (minutes: number | null) => {
    if (snoozeCtx) {
      respond(snoozeCtx.item, optionResponse(snoozeCtx.option), {
        snooze_minutes: minutes ?? minutesUntilTomorrow(),
      });
    }
    setSnoozeCtx(null);
  };

  /** An option picked from the "More…" menu (hidden ones included). */
  const pickMoreOption = (option: CheckInOption) => {
    const ctx = moreCtx;
    setMoreCtx(null);
    if (!ctx) return;
    if (option.response_category === 'snoozed') {
      setSnoozeCtx({ anchor: ctx.anchor, item: ctx.item, option });
      return;
    }
    respond(ctx.item, optionResponse(option));
  };

  const openCustomScore = () => {
    const ctx = moreCtx;
    setMoreCtx(null);
    if (!ctx) return;
    setCustomCategory('partial'); // "Partly" is the default
    setCustomScore('');
    setCustomCtx(ctx.item);
  };

  const parsedCustomScore = Number(customScore);
  const customScoreValid =
    customScore.trim() !== '' &&
    Number.isFinite(parsedCustomScore) &&
    parsedCustomScore >= 0 &&
    parsedCustomScore <= 10;

  /** Option-less graded response: { response_category, custom_score } with
   * NO option_id / presented_option_label — the server records the score
   * verbatim instead of deriving it from an option. */
  const submitCustomScore = () => {
    if (!customCtx || !customScoreValid) return;
    respond(customCtx, {
      response_category: customCategory,
      custom_score: parsedCustomScore,
    });
    setCustomCtx(null);
  };

  /** Pay an amount toward a debt row's outstanding total. */
  const drawDownDebt = async (item: CheckInQueueItem) => {
    if (respondingKey !== null) return; // a POST is already in flight
    if (item.habit_id === null) return; // debt is a habit ledger, always
    const raw = debtAmounts[itemKeyOf(item)] ?? String(item.debt_amount ?? '');
    const amount = Number(raw);
    if (!Number.isFinite(amount) || amount <= 0) return;
    setRespondingKey(itemKeyOf(item));
    setNotice(null);
    try {
      await axios.post(
        `${API_BASE_URL}/api/habits/${item.habit_id}/debt/drawdown`,
        {
          amount,
          // A STACK slice carries its own period_start — the payment must
          // land on THAT period. MERGE rows serve no period_start and keep
          // the server's FIFO default.
          ...(item.period_start ? { period_start: item.period_start } : {}),
        },
        { params: { tz_offset: tzOffset } },
      );
      await fetchQueue({ silent: true });
      setNotice(`Recorded ${amount} toward ${item.title}`);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to record toward the owed amount'));
    } finally {
      setRespondingKey(null);
    }
  };

  /** Wipe a habit's outstanding debt — an auditable FORGIVEN ledger entry,
   * never a deletion. Only reachable through the confirm dialog. */
  const forgiveDebt = async () => {
    if (respondingKey !== null) return; // a POST is already in flight
    const item = forgiveCtx;
    setForgiveCtx(null);
    if (!item || item.habit_id === null) return;
    setRespondingKey(itemKeyOf(item));
    setNotice(null);
    try {
      await axios.post(
        `${API_BASE_URL}/api/habits/${item.habit_id}/debt/forgive`,
        {},
        { params: { tz_offset: tzOffset } },
      );
      await fetchQueue({ silent: true });
      setNotice('Owed amount forgiven');
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to forgive the owed amount'));
    } finally {
      setRespondingKey(null);
    }
  };

  /** Bounded undo: retract the item's latest response. The server enforces
   * the same-logical-day bound — a 422 surfaces via the error alert. */
  const undoResponse = async (item: CheckInQueueItem) => {
    if (!item.last_response_event_id) return;
    setRespondingKey(itemKeyOf(item));
    try {
      await axios.post(
        `${API_BASE_URL}/api/check-ins/events/${item.last_response_event_id}/retract`,
        {},
        { params: { tz_offset: tzOffset } },
      );
      await fetchQueue({ silent: true });
      fetchActivity(); // an undo moves today's checked-in count back down
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to undo the response'));
    } finally {
      setRespondingKey(null);
    }
  };

  /**
   * Answer a commitment occurrence. This is the ONLY endpoint a commitment
   * slot ever posts to: /api/check-ins/respond is habit-only and 404s
   * "Habit not found" for an occurrence id. Returns the response body so the
   * reschedule flow can read `rescheduled_to` / `resolution`, or null when
   * the POST failed (the error alert already says why).
   */
  const respondCommitment = async (
    item: CheckInQueueItem,
    body: Record<string, unknown>,
    successNotice?: string,
  ): Promise<Record<string, any> | null> => {
    if (respondingKey !== null) return null; // a POST is already in flight
    setRespondingKey(itemKeyOf(item));
    setNotice(null);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/commitments/occurrences/${targetIdOf(item)}/respond`,
        body,
      );
      const rows = await fetchQueue({ silent: true });
      fetchActivity();
      advanceCardPast(rows);
      // A reschedule inserts a REPLACEMENT occurrence, which the queue can
      // serve on this same day — re-join so the new row is not left without
      // its meta (and therefore without its own Reschedule affordance).
      if (commitmentDay) await fetchCommitmentMeta(commitmentDay);
      if (successNotice) setNotice(successNotice);
      return res?.data ?? {};
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to record your commitment response'));
      return null;
    } finally {
      setRespondingKey(null);
    }
  };

  /** Wire values are lowercase: honored | partial | missed | rescheduled |
   * forgiven. HONORED and PARTIAL are the only "done" outcomes. */
  const answerCommitment = (
    item: CheckInQueueItem,
    state: 'honored' | 'missed' | 'forgiven',
    extra?: Record<string, unknown>,
  ) => {
    const outcome =
      state === 'honored' ? 'kept' : state === 'missed' ? 'not kept' : 'forgiven';
    return respondCommitment(item, { state, ...extra }, `${item.title} — ${outcome}`);
  };

  const openPartial = (item: CheckInQueueItem) => {
    const span = defaultSpanFor(item, commitmentMeta[targetIdOf(item)]);
    setPartialStart(span.start);
    setPartialEnd(span.end);
    setPartialCtx(item);
  };

  const partialValid =
    partialStart !== '' &&
    partialEnd !== '' &&
    Number.isFinite(Date.parse(partialStart)) &&
    Number.isFinite(Date.parse(partialEnd)) &&
    Date.parse(partialEnd) > Date.parse(partialStart);

  /** PARTIAL is the one state that carries the real span — the server scores
   * adherence as actual/planned from exactly these two instants. */
  const submitPartial = () => {
    const item = partialCtx;
    if (!item || !partialValid) return;
    setPartialCtx(null);
    respondCommitment(
      item,
      {
        state: 'partial',
        actual_start_at: new Date(partialStart).toISOString(),
        actual_end_at: new Date(partialEnd).toISOString(),
      },
      `${item.title} — partly kept`,
    );
  };

  const openReschedule = (item: CheckInQueueItem) => {
    setRescheduleMinutes(null);
    setPreview(null);
    setPreviewError(null);
    setRescheduleCtx(item);
  };

  const closeReschedule = () => {
    setRescheduleCtx(null);
    setPreviewError(null);
  };

  /** The commitment's own offsets when it has them; a neutral ladder when it
   * does not (the field is nullable and nothing server-side defaults it). */
  const rescheduleOffsets = useMemo(() => {
    if (!rescheduleCtx) return DEFAULT_SNOOZE_OFFSETS;
    const offsets = commitmentMeta[targetIdOf(rescheduleCtx)]?.snooze_offsets_minutes;
    return offsets && offsets.length > 0 ? offsets : DEFAULT_SNOOZE_OFFSETS;
  }, [rescheduleCtx, commitmentMeta]);

  /** Ask where the snooze WOULD land. Writes nothing — the whole point is
   * that the user sees the slot before anything moves. */
  const previewReschedule = async (minutes: number) => {
    const item = rescheduleCtx;
    if (!item) return;
    setRescheduleMinutes(minutes);
    setPreview(null);
    setPreviewError(null);
    setPreviewing(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/commitments/occurrences/${targetIdOf(
          item,
        )}/snooze-preview`,
        { snooze_minutes: minutes },
      );
      setPreview(res?.data ?? null);
    } catch (err: any) {
      setPreviewError(
        getApiErrorMessage(err, 'Could not work out where that snooze would land'),
      );
    } finally {
      setPreviewing(false);
    }
  };

  /** Commit the previewed move. An unresolved answer writes NOTHING
   * server-side, so the dialog stays open on the resolver's reasoning
   * instead of pretending the block moved. */
  const confirmReschedule = async () => {
    const item = rescheduleCtx;
    if (!item || rescheduleMinutes === null || !preview?.resolved) return;
    const data = await respondCommitment(item, {
      state: 'rescheduled',
      snooze_minutes: rescheduleMinutes,
    });
    if (!data) return;
    const moved = data.rescheduled_to;
    if (!moved) {
      setPreview(data.resolution ?? null);
      return;
    }
    closeReschedule();
    setNotice(
      moved.planned_start_at
        ? `${item.title} moved to ${formatLanding(moved.planned_start_at)}`
        : `${item.title} moved`,
    );
  };

  /** The carryover choice offered when no slot exists: owe it (MISSED, which
   * the carryover policy then presents) or write it off (FORGIVEN, excluded
   * from scoring and always auditable). */
  const chooseCarryover = async (state: 'missed' | 'forgiven') => {
    const item = rescheduleCtx;
    if (!item) return;
    closeReschedule();
    await answerCommitment(item, state);
  };

  // Card-mode keyboard navigation (arrow keys step through the queue).
  useEffect(() => {
    if (viewMode !== 'grid') return undefined;
    const onKey = (e: KeyboardEvent) => {
      // Keys typed into a form control belong to the control — the retro
      // date picker, the debt amount field and the custom-score dialog all
      // live inside card mode, and an ArrowLeft there must edit the field,
      // never flip the card underneath it.
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }
      if (e.key === 'ArrowRight') {
        setCardIndex((i) => Math.min(i + 1, Math.max(0, visibleItems.length - 1)));
      } else if (e.key === 'ArrowLeft') {
        setCardIndex((i) => Math.max(i - 1, 0));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewMode, visibleItems.length]);

  // Frozen batches of 8 (assigned by the daily snapshot server-side). Items
  // keep their batch for the whole logical day, so completing an item can
  // never pull the next batch forward. Muted items are simply absent from
  // their batch's rendering — the slots come back on unmute.
  const batchGroups = useMemo(() => {
    const map = new Map<number, CheckInQueueItem[]>();
    visibleItems.forEach((item) => {
      const list = map.get(item.batch) ?? [];
      list.push(item);
      map.set(item.batch, list);
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => a - b)
      .map(([batchIndex, batchItems]) => {
        const answeredCount = batchItems.filter((i) => i.answered === true).length;
        return {
          batchIndex,
          items: batchItems,
          doneCount: batchItems.filter((i) => i.done).length,
          // `answered` (not `done`) decides completion: Skip / Missed / an
          // answered-Missed commitment settle a slot without completing it.
          answeredCount,
          // Deliberate: completion is judged over the group's VISIBLE items —
          // muted items are session-hidden by the user's own intent.
          complete: batchItems.length > 0 && answeredCount === batchItems.length,
        };
      });
  }, [visibleItems]);

  // The current in-progress batch — the default-open accordion (list mode).
  const defaultOpenBatch = useMemo(
    () => batchGroups.find((g) => !g.complete)?.batchIndex,
    [batchGroups],
  );

  // Sticky auto-open: record the derived default as real expansion state the
  // moment it applies. An Undo in an EARLIER batch moves the first-incomplete
  // index backward, and without a stored entry the batch the user is working
  // in would re-derive to closed and snap shut mid-interaction. User toggles
  // always win — seed only where no entry exists yet.
  useEffect(() => {
    if (defaultOpenBatch === undefined) return;
    setBatchExpansion((prev) =>
      prev[defaultOpenBatch] === undefined
        ? { ...prev, [defaultOpenBatch]: true }
        : prev,
    );
  }, [defaultOpenBatch]);

  // W2.1: whether fully-answered batches render at all. Persisted in the
  // check_in namespace; read defensively (an old row's namespace may lack the
  // key) and fall back to session state when no PreferencesProvider exists.
  const showCompletedBatches = preferences
    ? (preferences.settings.check_in as Record<string, unknown> | undefined)
        ?.show_completed_batches === true
    : showCompletedFallback;

  const toggleShowCompletedBatches = () => {
    const next = !showCompletedBatches;
    if (preferences) {
      // Sibling-spread, never a bare literal: the namespace also carries
      // batch_size (and anything it gains later).
      const current = preferences.settings.check_in as Record<string, unknown>;
      preferences.setNamespace('check_in', { ...current, show_completed_batches: next });
    } else {
      setShowCompletedFallback(next);
    }
  };

  // The same measurable quick-log transports as the Habits page: chips /
  // custom amounts post /log/increment {value}; OPAQUE posts /log {value}.
  // The expressive complete/missed/snooze options above stay untouched.
  const renderQuickLog = (item: CheckInQueueItem, size: 'small' | 'medium') => {
    if (item.done) return null; // answered — the slot only marks the credit
    // Quick-log always writes TODAY's log — wrong for a catch-up day and
    // meaningless for a debt row, so both suppress it.
    if (item.item_kind === 'retro' || item.item_kind === 'debt') return null;
    // A commitment has no habit to log against at all.
    if (isCommitmentItem(item)) return null;
    const meta = metaOf(habitMeta, item);
    if (!meta) return null;
    const applicable =
      meta.measurement_kind === 'opaque' ||
      (isMeasurableKind(meta.measurement_kind) && quickIncrementValues(meta).length > 0);
    if (!applicable) return null;
    return (
      <Box mt={1.5}>
        <HabitQuickLogChips
          habit={meta}
          size={size}
          onLogged={async () => {
            // fetchActivity included: a period-completing increment counts as
            // answered server-side (completion credit), so the header's
            // finished-state fraction and streak must reconverge too.
            await Promise.all([
              fetchQueue({ silent: true }),
              fetchHabitMeta(),
              fetchActivity(),
            ]);
          }}
          onError={setError}
        />
      </Box>
    );
  };

  /**
   * A commitment is confirmed, not "responded to": it serves no options (the
   * habit response vocabulary does not fit a block of time) and every action
   * here posts to /api/commitments/occurrences/{occurrence}/respond.
   */
  const renderCommitmentActions = (
    item: CheckInQueueItem,
    size: 'small' | 'medium',
  ) => {
    // Kept (HONORED/PARTIAL) — the slot records the outcome, like a done
    // habit. MISSED / FORGIVEN / RESCHEDULED are answered but not done, and
    // the server treats an answer as correctable, so they keep the buttons.
    if (item.done) return null;
    const busy = respondingKey === itemKeyOf(item);
    const reschedulable = Boolean(commitmentMeta[targetIdOf(item)]?.reschedulable);
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          size={size}
          variant="contained"
          color="success"
          disabled={busy}
          aria-label={`Honored ${item.title}`}
          onClick={() => answerCommitment(item, 'honored')}
        >
          Honored
        </Button>
        <Button
          size={size}
          variant="outlined"
          color="warning"
          disabled={busy}
          aria-label={`Partially honored ${item.title}`}
          onClick={() => openPartial(item)}
        >
          Partially honored
        </Button>
        <Button
          size={size}
          variant="outlined"
          color="error"
          disabled={busy}
          aria-label={`Not honored ${item.title}`}
          onClick={() => answerCommitment(item, 'missed')}
        >
          Not honored
        </Button>
        {/* Offered only when the commitment allows it — respond() refuses a
            reschedule on a non-reschedulable commitment with a 409. */}
        {reschedulable && (
          <Button
            size={size}
            variant="outlined"
            color="info"
            disabled={busy}
            aria-label={`Reschedule ${item.title}`}
            onClick={() => openReschedule(item)}
          >
            Reschedule…
          </Button>
        )}
      </Stack>
    );
  };

  const renderOptions = (item: CheckInQueueItem, size: 'small' | 'medium') => {
    // ROUTING, not decoration: without this a commitment falls through to a
    // full habit card whose buttons post to the habit endpoint.
    if (isCommitmentItem(item)) return renderCommitmentActions(item, size);
    // A done item keeps its slot (frozen layout) but is no longer a prompt.
    if (item.done) return null;
    // Debt rows are settled by drawdown/forgiveness, never by a response.
    if (item.item_kind === 'debt') return null;
    // One-tap buttons show only queue-visible options, in served order; the
    // rest stay reachable through "More…".
    const visible = item.options.filter((option) => option.show_in_queue !== false);
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {visible.map((option) => (
          <Button
            key={option.id}
            size={size}
            variant={option.response_category === 'completed' ? 'contained' : 'outlined'}
            color={optionColor(option.response_category)}
            disabled={respondingKey === itemKeyOf(item)}
            aria-label={`${option.label} ${item.title}`}
            onClick={(e) => handleOption(e, item, option)}
          >
            {option.label}
          </Button>
        ))}
        <Button
          size={size}
          variant="outlined"
          color="inherit"
          disabled={respondingKey === itemKeyOf(item)}
          aria-label={`More options for ${item.title}`}
          onClick={(e) => setMoreCtx({ anchor: e.currentTarget, item })}
        >
          More…
        </Button>
      </Stack>
    );
  };

  /** Kind badge for catch-up/debt rows — deliberately OUTSIDE the
   * stateChips ladder below (that ladder renders at most one live-state
   * chip; the kind is a fixed property of the slot, not live state). */
  const kindBadge = (item: CheckInQueueItem) => {
    // Routed on target_type, and deliberately its own colour: a commitment is
    // neither a catch-up offer nor an owed amount.
    if (isCommitmentItem(item)) {
      return <Chip label="Commitment" size="small" variant="outlined" color="primary" />;
    }
    if (item.item_kind === 'retro') {
      return <Chip label="Catch-up" size="small" variant="outlined" color="secondary" />;
    }
    if (item.item_kind === 'debt') {
      const unit = metaOf(habitMeta, item)?.unit_label;
      const amount = item.debt_amount ?? 0;
      const covered = item.covered_period_count;
      return (
        <>
          <Chip
            label={`Owed ${amount}${unit ? ` ${unit}` : ''}`}
            size="small"
            variant="outlined"
            color="warning"
          />
          {/* Scope extra d: a MERGE row is one card standing in for several
              owed periods — say so, or the single chip understates the hole. */}
          {covered !== null && covered !== undefined && covered > 0 && (
            <Typography variant="caption" color="text.secondary">
              {`covers ${covered} owed period${covered === 1 ? '' : 's'}`}
              {item.oldest_period_start ? ` since ${item.oldest_period_start}` : ''}
            </Typography>
          )}
        </>
      );
    }
    return null;
  };

  /** D-P5-3: the habit's live streak, in its own period unit, on current
   * cards only. The server sends current_streak only on current habit slots,
   * but gate anyway so a retro/debt fixture reusing habit fields, or a
   * commitment (which has no recurrence), can never grow one. */
  const streakChip = (item: CheckInQueueItem) => {
    if (isCommitmentItem(item)) return null;
    if ((item.item_kind ?? 'current') !== 'current') return null;
    if (
      item.current_streak === null ||
      item.current_streak === undefined ||
      item.current_streak <= 0
    ) {
      return null;
    }
    return (
      <Tooltip title="Current streak">
        <Chip
          label={formatStreak(
            item.current_streak,
            item.recurrence_type,
            item.recurrence_interval ?? 1,
          )}
          size="small"
          variant="outlined"
          color="success"
        />
      </Tooltip>
    );
  };

  /** Bounded undo — offered only while a retractable event exists (the
   * server still enforces the same-logical-day bound). Deliberately NOT
   * filtered by item_kind: retro answers are retractable on the logical day
   * they were recorded, so catch-up cards keep the affordance too; a 422
   * surfaces its server detail through the error alert. */
  const renderUndo = (item: CheckInQueueItem) => {
    // No CheckInEvent backs a commitment answer (the outcome lives on the
    // occurrence), so there is nothing to retract.
    if (isCommitmentItem(item)) return null;
    if (!item.last_response || !item.last_response_event_id) return null;
    return (
      <Button
        size="small"
        variant="text"
        disabled={respondingKey === itemKeyOf(item)}
        aria-label={`Undo response for ${item.title}`}
        onClick={() => undoResponse(item)}
      >
        Undo
      </Button>
    );
  };

  /** The committed block itself, always visible: the state-chip ladder shows
   * at most one chip and spends it on Done / Opens at / Missed window, so the
   * span cannot rely on it. */
  const renderCommitmentSpan = (item: CheckInQueueItem) => {
    if (!isCommitmentItem(item)) return null;
    const span = commitmentSpanLabel(item);
    if (!span) return null;
    return (
      <Typography variant="caption" color="text.secondary" display="block">
        Committed {span}
      </Typography>
    );
  };

  /** Retro (catch-up) rows: which past day the answer lands on. Bounds are
   * [period_start, min(period_end - 1 day, yesterday)] — period_end is
   * exclusive — defaulting to the served effective day. */
  const renderRetroControls = (item: CheckInQueueItem) => {
    if (item.item_kind !== 'retro' || item.done) return null;
    const yesterday = addDaysYmd(todayLocalYmd(), -1);
    const max = item.period_end
      ? minYmd(addDaysYmd(item.period_end, -1), yesterday)
      : yesterday;
    const missed = item.effective_date ? daysAgo(item.effective_date) : null;
    return (
      <Box mt={1} mb={1}>
        {missed !== null && (
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Missed {missed} day{missed === 1 ? '' : 's'} ago
          </Typography>
        )}
        <TextField
          type="date"
          size="small"
          value={retroDateFor(item)}
          inputProps={{
            min: item.period_start ?? undefined,
            max,
            'aria-label': `Catch-up date for ${item.title}`,
          }}
          onChange={(e) =>
            setRetroDates((prev) => ({ ...prev, [itemKeyOf(item)]: e.target.value }))
          }
        />
      </Box>
    );
  };

  /** Debt rows: pay an amount toward the owed total, or forgive it (an
   * auditable FORGIVEN ledger entry via the confirm dialog). */
  const renderDebtControls = (item: CheckInQueueItem, size: 'small' | 'medium') => {
    if (item.item_kind !== 'debt') return null;
    const key = itemKeyOf(item);
    const raw = debtAmounts[key] ?? String(item.debt_amount ?? '');
    const amount = Number(raw);
    return (
      <Stack direction="row" spacing={1} alignItems="center" mt={1} flexWrap="wrap" useFlexGap>
        <TextField
          type="number"
          size="small"
          value={raw}
          sx={{ width: 110 }}
          inputProps={{ min: 0, step: 'any', 'aria-label': `Amount toward ${item.title}` }}
          onChange={(e) =>
            setDebtAmounts((prev) => ({ ...prev, [key]: e.target.value }))
          }
        />
        <Button
          size={size}
          variant="outlined"
          disabled={
            respondingKey === itemKeyOf(item) || !Number.isFinite(amount) || amount <= 0
          }
          aria-label={`Record toward it for ${item.title}`}
          onClick={() => drawDownDebt(item)}
        >
          Record toward it
        </Button>
        <Button
          size={size}
          color="warning"
          disabled={respondingKey === itemKeyOf(item)}
          aria-label={`Forgive owed amount for ${item.title}`}
          onClick={() => setForgiveCtx(item)}
        >
          Forgive…
        </Button>
      </Stack>
    );
  };

  // Live state chips over the frozen slot: done stays put marked done,
  // window-gated shows its opens-at time in place, snoozed shows its return.
  const stateChips = (item: CheckInQueueItem) => {
    const chips: React.ReactNode[] = [];
    if (item.done) {
      chips.push(
        <Chip
          key="done"
          label="Done"
          color="success"
          size="small"
          icon={<CheckCircleOutlineIcon />}
        />,
      );
    } else if (item.snooze_return_at) {
      chips.push(
        <Chip
          key="snoozed"
          label={`Snoozed until ${formatInstant(item.snooze_return_at)}`}
          color="info"
          size="small"
          variant="outlined"
        />,
      );
    } else if (item.last_response && RESPONSE_CHIP_LABELS[item.last_response]) {
      chips.push(
        <Chip
          key="answered"
          label={RESPONSE_CHIP_LABELS[item.last_response]}
          color="default"
          size="small"
          variant="outlined"
          icon={<HistoryToggleOffIcon />}
        />,
      );
    } else if (item.pending_window && item.window_start_minute !== null) {
      chips.push(
        <Chip
          key="pending"
          label={`Opens at ${formatMinuteOfDay(item.window_start_minute)}`}
          size="small"
          variant="outlined"
        />,
      );
    } else if (item.missed_window) {
      chips.push(
        <Chip key="missed" label="Missed window" color="warning" size="small" variant="outlined" />,
      );
    } else if (item.window_label && !isCommitmentItem(item)) {
      // A commitment's span has its own line — a third rendering of the same
      // "17:00-19:00" would be noise, not information.
      chips.push(<Chip key="window" label={item.window_label} size="small" variant="outlined" />);
    }
    return chips.length > 0 ? <>{chips}</> : null;
  };

  /** "Batch 2 · #3 · #11 overall · Priority 2" — the item's position within
   * its own batch, its global position across every batch of the logical day,
   * and the habit's priority. All three are frozen by the daily snapshot. */
  const rankLabel = (item: CheckInQueueItem) => {
    const position =
      `Batch ${item.batch + 1} · #${item.batch_position + 1}`
      + ` · #${item.rank + 1} overall`;
    // `priority` is a neutral filler on a commitment slot (always 0), not a
    // real ranking — printing "Priority 0" would state something untrue.
    return isCommitmentItem(item) ? position : `${position} · Priority ${item.priority}`;
  };

  const header = (
    <Box
      sx={pageHeaderSx}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      gap={1}
    >
      <Box>
        <Typography variant="h4" component="h1">
          Check-In
        </Typography>
        <Typography variant="body2" color="text.secondary">
          What to review right now — using your real local time.
        </Typography>
        {/* W2.2: the ritual's own numbers. Each is explained through the
            canonical glossary idiom (MetricInfo), never ad-hoc prose; the
            per-period rollup lives in a sibling popover because the glossary
            popover holds definitions only. */}
        {activity && (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 0.5 }}
          >
            {activity.streak_current > 0 && (
              <>
                <Chip
                  size="small"
                  variant="outlined"
                  color="success"
                  label={`${activity.streak_current}-day streak`}
                />
                <MetricInfo metric="check_in_streak" />
                <Typography variant="body2" color="text.secondary">
                  ·
                </Typography>
              </>
            )}
            {/* Finished-state roster progress — the streak's own notion of a
                finished day. Plainly labelled and deliberately carrying NO
                glossary icon: today.answered counts log-completions with
                zero button presses, which is not the checked-in metric. */}
            <Typography variant="body2" color="text.secondary">
              {`${activity.today.answered}/${activity.today.roster_size} answered today`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ·
            </Typography>
            {/* The press-based checked-in metric itself — the only number
                allowed under the checked_in_count glossary icon, and the same
                reading the popover's Today row shows. */}
            <Button
              size="small"
              variant="text"
              color="inherit"
              aria-label="Checked-in counts by period"
              onClick={(e) => setActivityAnchor(e.currentTarget)}
              sx={{ textTransform: 'none', p: 0, minWidth: 0, color: 'text.secondary' }}
            >
              {`${activity.checked_in.day} checked in today`}
            </Button>
            <MetricInfo metric="checked_in_count" />
          </Stack>
        )}
      </Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Tooltip title={QUEUE_CADENCE_TITLE}>
          <IconButton
            aria-label="How the check-in queue is built"
            onClick={(e) => setCadenceAnchor(e.currentTarget)}
          >
            <InfoOutlinedIcon />
          </IconButton>
        </Tooltip>
        {tagOptions.length > 0 && (
          <>
            <Tooltip title="Mute tags for this session">
              <IconButton
                aria-label="Mute tags"
                color={mutedTags.length > 0 ? 'primary' : 'default'}
                onClick={(e) => setMuteMenuAnchor(e.currentTarget)}
              >
                <VisibilityOffIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={muteMenuAnchor}
              open={Boolean(muteMenuAnchor)}
              onClose={() => setMuteMenuAnchor(null)}
            >
              {tagOptions.map((tag) => (
                <MenuItem key={tag} onClick={() => toggleMutedTag(tag)}>
                  <Checkbox
                    edge="start"
                    size="small"
                    checked={mutedSet.has(tag)}
                    readOnly
                    tabIndex={-1}
                    inputProps={{ 'aria-label': `Mute tag ${tag}` }}
                    sx={{ pointerEvents: 'none' }}
                  />
                  <ListItemText>{tag}</ListItemText>
                </MenuItem>
              ))}
              {mutedTags.length > 0 && <Divider />}
              {mutedTags.length > 0 && (
                <MenuItem onClick={() => setMutedTags([])}>Unmute all</MenuItem>
              )}
            </Menu>
          </>
        )}
        <Tooltip title="Refresh queue">
          <IconButton aria-label="Refresh queue" onClick={() => fetchQueue()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <ViewModeToggle
          value={viewMode}
          onChange={setViewMode}
          ariaLabel="check-in view mode"
          isOverride={viewModeControls.isOverride}
          onUseDefault={viewModeControls.useDefault}
        />
      </Stack>
    </Box>
  );

  if (loading) {
    return (
      <PageShell maxWidth="narrow">
        {header}
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  const current = visibleItems[cardIndex];

  // W3.2: the settled-state specifics, or [] when the insight is unavailable
  // (in which case nothing extra renders — never invented reasons).
  const insightNotes = insight ? insightReasonLines(insight) : [];

  // W2.1 (list mode): fully-answered batches collapse to a summary and hide
  // behind one control; the in-progress batch is never hidden.
  const completedBatchCount = batchGroups.filter((g) => g.complete).length;
  const renderedBatchGroups = showCompletedBatches
    ? batchGroups
    : batchGroups.filter((g) => !g.complete);

  return (
    <PageShell maxWidth="narrow">
      {header}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {/* Scope extra a: the otherwise-silent dead end. With no account
          timezone the materialiser swallows every commitment, so their
          confirmations simply never appear here — name it and hand over the
          door (Planner's timezone alert is the idiom). */}
      {insight?.queue.commitments_suppressed_reason === 'missing_timezone' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>No account timezone is set</AlertTitle>
          Commitment confirmations are missing from this queue: without a
          timezone the server will not guess wall-clock times, so no
          commitment occurrences are materialised. Set it in{' '}
          <strong>Profile → Hour classes → Account timezone</strong>.
          <Box sx={{ mt: 1 }}>
            <Button size="small" variant="outlined" onClick={() => navigate('/profile')}>
              Open Profile
            </Button>
          </Box>
        </Alert>
      )}

      {mutedOutCount > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 1 }}
        >
          {mutedOutCount} check-in{mutedOutCount === 1 ? '' : 's'} hidden by muted
          tags (this session only).
        </Typography>
      )}

      {items.length === 0 ? (
        // A failed load renders the error alert alone: an unloaded queue is
        // NOT an empty queue, so no cheerful caught-up card may stand under
        // the failure banner claiming nothing is due.
        error ? null : (
        <Card>
          <CardContent sx={cardContentPaddingSx}>
            <Box display="flex" flexDirection="column" alignItems="center" gap={1} py={4}>
              <CheckCircleOutlineIcon color="success" fontSize="large" />
              <Typography variant="h6">You&apos;re all caught up</Typography>
              {insightNotes.length > 0 ? (
                // W3.2: the real reasons, from the user's own data. The
                // cheerful headline stays; the boilerplate beneath it goes.
                <Box>
                  {insightNotes.map((line) => (
                    <Typography
                      key={line}
                      variant="body2"
                      color="text.secondary"
                      textAlign="center"
                    >
                      {line}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  No habits need a check-in right now. Enable check-in on a habit and add
                  windows from the Habits page to see it here.
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
        )
      ) : visibleItems.length === 0 ? (
        // Everything left is muted out — distinct from all-caught-up, and a
        // pure view state: unmuting brings the items straight back.
        <Card>
          <CardContent sx={cardContentPaddingSx}>
            <Box display="flex" flexDirection="column" alignItems="center" gap={1} py={4}>
              <VisibilityOffIcon color="disabled" fontSize="large" />
              <Typography variant="h6">Everything left is muted</Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                All remaining check-ins carry a muted tag. Unmute tags from the
                control above to see them again.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        // Card mode — one habit at a time.
        current && (
          <Card data-testid="checkin-card">
            <CardContent sx={cardContentPaddingSx}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="overline" color="text.secondary">
                  {cardIndex + 1} of {visibleItems.length}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  {kindBadge(current)}
                  {streakChip(current)}
                  {stateChips(current)}
                  {renderUndo(current)}
                </Stack>
              </Stack>
              <Typography variant="h5" component="h2" gutterBottom>
                {current.title}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                {rankLabel(current)}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {current.explanation}
              </Typography>
              {/* target_count/completed_count are habit-shaped fillers on a
                  commitment slot — the committed span is the real fact. */}
              {isCommitmentItem(current) ? (
                renderCommitmentSpan(current)
              ) : (
                <Typography variant="body2" gutterBottom>
                  Progress: {current.completed_count}/{current.target_count}
                </Typography>
              )}
              <Divider sx={{ my: 2 }} />
              {renderRetroControls(current)}
              {renderOptions(current, 'medium')}
              {renderDebtControls(current, 'medium')}
              {renderQuickLog(current, 'medium')}
              <Stack direction="row" justifyContent="space-between" mt={3}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  disabled={cardIndex === 0}
                  onClick={() => setCardIndex((i) => Math.max(i - 1, 0))}
                >
                  Back
                </Button>
                {/* Card navigation, NOT a check-in response. It was labelled
                    "Skip for now", which collided with the server-seeded
                    "Skip" option a few pixels above: it recorded nothing, was
                    forgotten on reload, and is necessarily disabled on the
                    last card — so it read as a broken Skip button. Use the
                    "Skip" option above to actually record a skip. */}
                <Button
                  endIcon={<ArrowForwardIcon />}
                  disabled={cardIndex >= visibleItems.length - 1}
                  onClick={() =>
                    setCardIndex((i) => Math.min(i + 1, visibleItems.length - 1))
                  }
                >
                  Next
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )
      ) : (
        // List mode — the day's frozen batches as CONTROLLED accordions
        // (W2.1). The in-progress batch opens by default; a fully-answered
        // batch collapses to a one-line summary and hides behind the single
        // Show control below. Done and window-gated items keep their slots.
        <Box data-testid="checkin-list">
          {completedBatchCount > 0 && (
            <Box display="flex" justifyContent="flex-end" mb={1}>
              <Button size="small" variant="text" onClick={toggleShowCompletedBatches}>
                {`${showCompletedBatches ? 'Hide' : 'Show'} ${completedBatchCount} completed batch${
                  completedBatchCount === 1 ? '' : 'es'
                }`}
              </Button>
            </Box>
          )}
          {renderedBatchGroups.map((group) => (
            <Accordion
              key={group.batchIndex}
              expanded={
                batchExpansion[group.batchIndex] ??
                (!group.complete && group.batchIndex === defaultOpenBatch)
              }
              onChange={(_event, isExpanded) =>
                setBatchExpansion((prev) => ({ ...prev, [group.batchIndex]: isExpanded }))
              }
              disableGutters
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`checkin-batch-${group.batchIndex}-content`}
                id={`checkin-batch-${group.batchIndex}-header`}
              >
                <Stack direction="row" spacing={1.5} alignItems="baseline">
                  <Typography variant="subtitle1">Batch {group.batchIndex + 1}</Typography>
                  {group.complete ? (
                    // The one-line completion summary; expanding it below
                    // reopens the full audit (chips, Undo) untouched.
                    <Typography variant="body2" color="text.secondary">
                      {`${group.answeredCount}/${group.items.length} answered · ✓`}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {group.doneCount} / {group.items.length}
                    </Typography>
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails id={`checkin-batch-${group.batchIndex}-content`}>
                <Stack spacing={2}>
                  {group.items.map((item) => (
                    <Card key={itemKeyOf(item)} variant="outlined">
                      <CardContent sx={cardContentPaddingSx}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          justifyContent="space-between"
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          spacing={1.5}
                        >
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="subtitle1">{item.title}</Typography>
                              {kindBadge(item)}
                              {streakChip(item)}
                              {stateChips(item)}
                              {renderUndo(item)}
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                              {rankLabel(item)} · {item.explanation}
                              {isCommitmentItem(item)
                                ? ''
                                : ` · ${item.completed_count}/${item.target_count}`}
                            </Typography>
                            {renderCommitmentSpan(item)}
                            {renderRetroControls(item)}
                            {renderDebtControls(item, 'small')}
                            {renderQuickLog(item, 'small')}
                          </Box>
                          {renderOptions(item, 'small')}
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* W3.2, the finished half: the whole served day is answered, so say
          why nothing more is on offer — the batches above have collapsed and
          this is the honest answer to "where are my catch-up items?".
          Renders nothing without insight (graceful catch, empty lines). */}
      {items.length > 0 && queueSettled && insightNotes.length > 0 && (
        <Card sx={{ mt: 2 }}>
          <CardContent sx={cardContentPaddingSx}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <CheckCircleOutlineIcon color="success" fontSize="small" />
              <Typography variant="subtitle1">All checked in for today</Typography>
            </Stack>
            {insightNotes.map((line) => (
              <Typography key={line} variant="body2" color="text.secondary">
                {line}
              </Typography>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Checked-in rollups (W2.2). The MetricInfo popover is glossary-only,
          so the per-period counts live in this sibling popover, opened from
          the header's checked-in reading. */}
      {activity && (
        <Popover
          open={Boolean(activityAnchor)}
          anchorEl={activityAnchor}
          onClose={() => setActivityAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box sx={[panelPaddingSx, { minWidth: 220 }]}>
            <Typography variant="subtitle2" gutterBottom>
              Checked-in items
            </Typography>
            {(
              [
                ['Today', activity.checked_in.day],
                ['This week', activity.checked_in.week],
                ['This month', activity.checked_in.month],
                ['This quarter', activity.checked_in.quarter],
                ['This year', activity.checked_in.year],
              ] as [string, number][]
            ).map(([label, value]) => (
              <Stack
                key={label}
                direction="row"
                justifyContent="space-between"
                spacing={3}
              >
                <Typography variant="body2" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="body2">{value}</Typography>
              </Stack>
            ))}
          </Box>
        </Popover>
      )}

      {/* Cadence explainer. Every string is printed verbatim from
          checkin/queueCadence.json — never written inline here. That single
          source is mirrored in docs/60 USER_GUIDE.md and locked by
          checkin/queueCadence.test.ts, so the copy cannot drift. */}
      <Popover
        open={Boolean(cadenceAnchor)}
        anchorEl={cadenceAnchor}
        onClose={() => setCadenceAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={[panelPaddingSx, { maxWidth: 420 }]}>
          <Typography variant="subtitle1" gutterBottom>
            {QUEUE_CADENCE_TITLE}
          </Typography>
          {QUEUE_CADENCE_ENTRIES.map((item) => (
            <Box key={item.key} sx={{ mt: 1.5 }}>
              <Typography variant="subtitle2">{item.heading}</Typography>
              <Typography variant="body2" color="text.secondary">
                {item.body}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>

      <Menu
        open={Boolean(snoozeCtx)}
        anchorEl={snoozeCtx?.anchor ?? null}
        onClose={() => setSnoozeCtx(null)}
      >
        {SNOOZE_PRESETS.map((preset) => (
          <MenuItem key={preset.label} onClick={() => pickSnooze(preset.minutes)}>
            {preset.label}
          </MenuItem>
        ))}
      </Menu>

      {/* "More…": EVERY active option — the show_in_queue=false ones the
          one-tap row hides included — plus the option-less custom score. */}
      <Menu
        open={Boolean(moreCtx)}
        anchorEl={moreCtx?.anchor ?? null}
        onClose={() => setMoreCtx(null)}
      >
        {(moreCtx?.item.options ?? []).map((option) => (
          <MenuItem key={option.id} onClick={() => pickMoreOption(option)}>
            {option.label}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={openCustomScore}>Custom score…</MenuItem>
      </Menu>

      {/* Custom score: posts { response_category, custom_score } with NO
          option_id / presented_option_label. */}
      <Dialog
        open={Boolean(customCtx)}
        onClose={() => setCustomCtx(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Custom score</DialogTitle>
        <DialogContent>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={customCategory}
            onChange={(event, value) => {
              if (value) setCustomCategory(value);
            }}
            aria-label="Custom score category"
            sx={{ mb: 2, flexWrap: 'wrap' }}
          >
            {CUSTOM_SCORE_CATEGORIES.map((cat) => (
              <ToggleButton key={cat.value} value={cat.value}>
                {cat.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField
            type="number"
            size="small"
            fullWidth
            value={customScore}
            inputProps={{ min: 0, max: 10, step: 0.1, 'aria-label': 'Custom score value' }}
            helperText="0–10, decimals allowed (e.g. 2.5)"
            onChange={(e) => setCustomScore(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomCtx(null)}>Cancel</Button>
          <Button variant="contained" disabled={!customScoreValid} onClick={submitCustomScore}>
            Save score
          </Button>
        </DialogActions>
      </Dialog>

      {/* Partly kept: the block ran, but not as promised. The server scores
          adherence as actual/planned from exactly this span, so it is asked
          for rather than guessed. */}
      <Dialog
        open={Boolean(partialCtx)}
        onClose={() => setPartialCtx(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>When did {partialCtx?.title} actually run?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {partialCtx
              ? `Committed ${commitmentSpanLabel(partialCtx) ?? 'block'}. Adjust to the span you really kept.`
              : ''}
          </DialogContentText>
          <Stack spacing={2}>
            <TextField
              type="datetime-local"
              size="small"
              fullWidth
              label="Actual start"
              InputLabelProps={{ shrink: true }}
              value={partialStart}
              onChange={(e) => setPartialStart(e.target.value)}
            />
            <TextField
              type="datetime-local"
              size="small"
              fullWidth
              label="Actual end"
              InputLabelProps={{ shrink: true }}
              value={partialEnd}
              onChange={(e) => setPartialEnd(e.target.value)}
              error={partialStart !== '' && partialEnd !== '' && !partialValid}
              helperText={
                partialStart !== '' && partialEnd !== '' && !partialValid
                  ? 'The end has to be after the start.'
                  : ' '
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPartialCtx(null)}>Cancel</Button>
          <Button variant="contained" disabled={!partialValid} onClick={submitPartial}>
            Save span
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reschedule: the preview is asked FIRST and writes nothing, so the
          user commits to a slot they have already seen. */}
      <Dialog
        open={Boolean(rescheduleCtx)}
        onClose={closeReschedule}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reschedule {rescheduleCtx?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            Pick how far to push it. Nothing moves until you confirm the slot.
          </DialogContentText>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {rescheduleOffsets.map((minutes) => (
              <Button
                key={minutes}
                size="small"
                variant={rescheduleMinutes === minutes ? 'contained' : 'outlined'}
                aria-label={`Snooze by ${offsetLabel(minutes)}`}
                onClick={() => previewReschedule(minutes)}
              >
                {offsetLabel(minutes)}
              </Button>
            ))}
          </Stack>
          {previewing && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Checking where it would land…
            </Typography>
          )}
          {previewError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {previewError}
            </Alert>
          )}
          {preview && !previewing && preview.resolved && preview.start_at && (
            <Box mt={2}>
              <Typography variant="body2">
                Will move to {formatLanding(preview.start_at)}
                {preview.end_at ? ` – ${formatInstant(preview.end_at)}` : ''}
              </Typography>
              {preview.rolled && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Rolled forward {preview.rolled_minutes} minute
                  {preview.rolled_minutes === 1 ? '' : 's'} to the first slot that fits.
                </Typography>
              )}
              {(preview.warnings ?? []).map((warning) => (
                <Typography
                  key={warning}
                  variant="caption"
                  color="warning.main"
                  display="block"
                >
                  {warning}
                </Typography>
              ))}
            </Box>
          )}
          {/* No slot: the resolver's reasoning, then the carryover choice —
              never an invented landing time. */}
          {preview && !previewing && !preview.resolved && (
            <Box mt={2}>
              <Alert severity="warning">{preview.reason}</Alert>
              {carryoverSentence(preview) && (
                <Typography variant="body2" sx={{ mt: 1.5 }}>
                  {carryoverSentence(preview)}
                </Typography>
              )}
              <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  disabled={respondingKey !== null}
                  onClick={() => chooseCarryover('missed')}
                >
                  Carry it over (not honored)
                </Button>
                <Button
                  size="small"
                  variant="text"
                  disabled={respondingKey !== null}
                  onClick={() => chooseCarryover('forgiven')}
                >
                  Forgive this block
                </Button>
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReschedule}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!preview?.resolved || respondingKey !== null}
            onClick={confirmReschedule}
          >
            Move it
          </Button>
        </DialogActions>
      </Dialog>

      {/* Forgiveness is an amnesty, not an eraser — say so before writing. */}
      <Dialog
        open={Boolean(forgiveCtx)}
        onClose={() => setForgiveCtx(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Forgive this owed amount?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This writes an auditable FORGIVEN entry to the habit&apos;s debt
            ledger and zeroes what is outstanding. It never deletes history —
            the amnesty itself stays on the record.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForgiveCtx(null)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={respondingKey !== null}
            onClick={forgiveDebt}
          >
            Forgive debt
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default CheckIn;
